import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { ZIndex } from "config/zIndex";
import { CustomIcon } from "components/icon/customIcon.component";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";
import { useParentZIndex } from "components/designV2/stacking/stackingZIndexContext";

export type DropdownOption = {
  value: string;
  label: string;
};

/** Props for the dropdown component. */
export interface DropdownProps {
  ariaLabel?: string;
  label?: string;
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

const DEFAULT_MAX_HEIGHT = 260;
const MENU_MARGIN_PX = 4;
const ESTIMATED_OPTION_HEIGHT_PX = 56;
let dropdownListboxIdCounter = 0;

interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

/** Dropdown input with selectable options. */
export const Dropdown = ({
  ariaLabel,
  label,
  options,
  value,
  placeholder,
  disabled,
  fullWidth = false,
  onChange,
  onBlur,
  onFocus,
}: DropdownProps): JSX.Element => {
  const { t } = useTranslation();
  const parentZIndex = useParentZIndex();
  const menuZIndex = parentZIndex != null ? parentZIndex + 1 : ZIndex.dropdown;
  const listboxIdRef = useRef<string>("");
  if (!listboxIdRef.current) {
    dropdownListboxIdCounter += 1;
    listboxIdRef.current = `dropdown-listbox-${dropdownListboxIdCounter}`;
  }
  const listboxId = listboxIdRef.current;
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const placeholderText = placeholder ?? t("General.SelectOption");

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const updateMenuPosition = useCallback((): void => {
    if (!dropdownRef.current) return;

    const triggerRect = dropdownRef.current.getBoundingClientRect();
    const estimatedContentHeight = Math.min(
      DEFAULT_MAX_HEIGHT,
      Math.max(options.length, 1) * ESTIMATED_OPTION_HEIGHT_PX,
    );
    const spaceBelow = window.innerHeight - triggerRect.bottom - MENU_MARGIN_PX;
    const spaceAbove = triggerRect.top - MENU_MARGIN_PX;
    const shouldOpenUpwards =
      spaceBelow < estimatedContentHeight && spaceAbove > spaceBelow;
    const top = shouldOpenUpwards
      ? triggerRect.top - estimatedContentHeight - MENU_MARGIN_PX
      : triggerRect.bottom + MENU_MARGIN_PX;

    setMenuPosition({
      top: Math.max(8, top),
      left: triggerRect.left,
      width: triggerRect.width,
    });
  }, [options.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      const isInsideTrigger = Boolean(
        dropdownRef.current && dropdownRef.current.contains(target),
      );
      const isInsideMenu = Boolean(
        listRef.current && listRef.current.contains(target),
      );
      if (isInsideTrigger || isInsideMenu) return;
      if (!isOpen) return;
      setIsOpen(false);
      setActiveIndex(-1);
      onBlur?.();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onBlur]);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();

    const handleWindowChange = (): void => {
      updateMenuPosition();
    };

    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);
    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!disabled || !isOpen) return;
    setIsOpen(false);
    setActiveIndex(-1);
  }, [disabled, isOpen]);

  const handleToggle = (): void => {
    if (disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      setActiveIndex(
        next
          ? Math.max(
              0,
              options.findIndex((option) => option.value === value),
            )
          : -1,
      );
      if (next) onFocus?.();
      if (!next) onBlur?.();
      return next;
    });
  };

  const handleSelect = (selectedValue: string): void => {
    if (disabled) return;
    onChange?.(selectedValue);
    setIsOpen(false);
    setActiveIndex(-1);
    onBlur?.();
  };

  const moveActiveIndex = (step: number): void => {
    if (options.length === 0) return;
    setActiveIndex((prev) => {
      if (prev < 0) return step > 0 ? 0 : options.length - 1;
      const next = prev + step;
      if (next < 0) return options.length - 1;
      if (next >= options.length) return 0;
      return next;
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    if (!isOpen && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      handleToggle();
      return;
    }

    if (event.key === "Escape") {
      if (!isOpen) return;
      setIsOpen(false);
      setActiveIndex(-1);
      onBlur?.();
      return;
    }

    if (!isOpen) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActiveIndex(1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActiveIndex(-1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      if (options.length > 0) setActiveIndex(0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      if (options.length > 0) setActiveIndex(options.length - 1);
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < options.length) {
        handleSelect(options[activeIndex].value);
      }
    }
  };

  return (
    <Container
      ref={dropdownRef}
      aria-disabled={disabled}
      $fullWidth={fullWidth}
    >
      {label && <Label>{label}</Label>}
      <Trigger
        role="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        tabIndex={disabled ? -1 : 0}
        $isOpen={isOpen}
        $disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {selectedOption ? (
          <ValueText $disabled={disabled}>{selectedOption.label}</ValueText>
        ) : (
          <Placeholder>{placeholderText}</Placeholder>
        )}
        <IconWrapper>
          <CustomIcon
            name={isOpen ? "v2/up-chevron" : "v2/down-chevron"}
            size="24px"
            color={ColorV2.Blue5}
          />
        </IconWrapper>
      </Trigger>
      {isOpen &&
        menuPosition &&
        createPortal(
          <List
            id={listboxId}
            ref={listRef}
            role="listbox"
            $isOpen={isOpen}
            $zIndex={menuZIndex}
            $isPortal
            style={{
              position: "fixed",
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`,
            }}
          >
            <ListScroll>
              {options.map((option) => {
                const isSelected = option.value === value;
                const isActive =
                  activeIndex >= 0 &&
                  options[activeIndex]?.value === option.value;
                return (
                  <Option
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    $isSelected={isSelected}
                    $isActive={isActive}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </Option>
                );
              })}
            </ListScroll>
          </List>,
          document.body,
        )}
    </Container>
  );
};

const Container = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  width: ${(props) => (props.$fullWidth ? "100%" : `${V2_INPUT_WIDTH_PX}px`)};
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
`;

const Trigger = styled.div<{ $disabled?: boolean; $isOpen?: boolean }>`
  height: ${V2_INPUT_HEIGHT_PX}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  ${(props) =>
    props.$isOpen &&
    css`
      box-shadow: 0 6px 16px rgba(15, 42, 64, 0.12);
      border-color: ${ColorV2.Blue5};
    `}

  ${(props) =>
    props.$disabled &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ValueText = styled.span<{ $disabled?: boolean }>`
  ${TypographyV2.b2}
  color: ${(props) => (props.$disabled ? ColorV2.Blue5 : ColorV2.Blue1)};
`;

const Placeholder = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue5};
`;

const List = styled.ul<{
  $isOpen?: boolean;
  $zIndex: number;
  $isPortal?: boolean;
}>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 4px 0 0 0;
  padding: 8px 0;
  list-style: none;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 12px 24px rgba(15, 42, 64, 0.12);
  max-height: ${DEFAULT_MAX_HEIGHT}px;
  overflow: hidden;
  z-index: ${(props) => props.$zIndex};
  display: ${(props) => (props.$isOpen ? "block" : "none")};
`;

const ListScroll = styled.div`
  max-height: ${DEFAULT_MAX_HEIGHT}px;
  overflow-y: auto;
  padding: 8px 0;
  padding-right: 8px;
  box-sizing: border-box;

  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${ColorV2.Blue7};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue6};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue5};
  }
`;

const Option = styled.li<{ $isSelected?: boolean; $isActive?: boolean }>`
  padding: 12px 16px;
  margin: 4px 8px;
  border-radius: 12px;
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background-color: ${ColorV2.Blue6};
    color: ${ColorV2.Blue1};
  }

  ${(props) =>
    props.$isActive &&
    css`
      background-color: ${ColorV2.Blue6};
    `}

  ${(props) =>
    props.$isSelected &&
    css`
      background-color: ${ColorV2.Blue1};
      color: ${ColorV2.White};
    `}
`;
